// © Microsoft Corporation. All rights reserved.

import {
    DesignSystem,
    ElevationMultiplier,
    applyElevation,
    highContrastSelector,
    neutralFillActive,
    neutralFillHover,
    neutralForegroundRest,
    neutralLayerCard
} from "@microsoft/fast-components-styles-msft";
import { applyLocalizedProperty, toPx } from "@microsoft/fast-jss-utilities";
import { iconContainerDefaultColor, likeDislikeIconBackroundColor, moreOptionsIconColors } from "./SharingToolbar.constants";

import { CardContextMenuClassNameContract } from "@msnews/card-context-menu";
import { ComponentStyles } from "@microsoft/fast-jss-manager";
import { SharingToolbarClassNames } from "./";

// reduce listItem dimension to 32, remove margin for iconListHorizontal (3px) and add list icon margin (in between) dinamically hor/vert
const iconListItemDimension = 32;
const iconContainerDimension = 32;
const iconImageDimension = 18;
const iconPadding = 10;

/**
 * The SharingToolbar styles
 * @type {ComponentStyles<SharingToolbarClassNameContract, DesignSystem>}
 */
export const SharingToolbarStyles: ComponentStyles<SharingToolbarClassNames, DesignSystem> = (config: DesignSystem) => {
    return {
        icon_none: {},
        icon_like: {},
        icon_dislike: {},
        iconListHorizontal: {
            "list-style": "none",
            padding: "0",
            margin: "0",
            "text-align": "center",
            "& $iconListItem": {
                [applyLocalizedProperty("margin-right", "margin-left", config.direction)]: toPx(iconPadding)
            }
        },
        iconListVertical: {
            float: "left",
            height: "auto",
            width: toPx(iconListItemDimension),
            position: "absolute",
            "& $iconListItem": {
                "margin-bottom": toPx(iconPadding)
            }
        },
        iconListItem: {
            "align-items": "center",
            display: "inline-flex",
            height: toPx(iconListItemDimension),
            "justify-content": "center",
            width: toPx(iconListItemDimension),
            "&:hover": {
                cursor: "pointer"
            }
        },
        shareDialog: {
            "background-color": "#f00"
        },
        iconContainer: {
            cursor: "pointer",
            border: "unset",
            padding: "unset",
            "background-color": iconContainerDefaultColor,
            "border-radius": "50%",
            color: neutralForegroundRest,
            height: toPx(iconContainerDimension),
            "justify-content": "center",
            position: "relative",
            width: toPx(iconContainerDimension),
            "$icon_none &": {
                "background-color": likeDislikeIconBackroundColor.none
            },
            "$icon_like &": {
                "background-color": likeDislikeIconBackroundColor.like
            },
            "$icon_dislike &": {
                "background-color": likeDislikeIconBackroundColor.dislike
            },
            "& img": {
                "vertical-align": "unset"
            }
        },
        highContrast: {
            [highContrastSelector]: {
                background: "Highlight",
                border: "1px solid Highlight",
                "border-radius": "50%",
                "& svg": {
                    fill: "Highlight"
                }
            }
        },
        iconImage: {
            bottom: "0",
            left: "0",
            margin: "7px",
            right: "0",
            top: "0",
            height: toPx(iconImageDimension),
            width: toPx(iconImageDimension)
        },
        moreOptions: {
            height: toPx(iconContainerDimension),
            width: toPx(iconContainerDimension),
            [highContrastSelector]: {
                width: toPx(iconContainerDimension - 2),
                height: toPx(iconContainerDimension - 2)
            }
        }
    };
};

/**
 * The context menu styles
 * @type {ComponentStyles<CardContextMenuClassNameContract, DesignSystem>}
 */
export const contextMenuStyles: Partial<ComponentStyles<CardContextMenuClassNameContract, DesignSystem>> = {
    menuContainer: {
        "background-color": moreOptionsIconColors.background,
        "border-radius": "50%",
        height: toPx(iconContainerDimension),
        width: toPx(iconContainerDimension),
        [highContrastSelector]: {
            width: toPx(iconContainerDimension - 2),
            height: toPx(iconContainerDimension - 2)
        }
    },
    settingButton: {
        background: "none",
        border: "0",
        cursor: "pointer",
        display: "flex",
        padding: "0",
        height: toPx(iconContainerDimension),
        margin: "0",
        width: toPx(iconContainerDimension),
        "& svg": {
            color: moreOptionsIconColors.foreground,
            display: "flex",
            margin: "auto",
            height: "inherit",
            width: toPx(iconImageDimension),
            "& path": {
                fill: "currentColor"
            }
        }
    },
    contextMenu: {
        ...applyElevation(ElevationMultiplier.e11),
        "background-color": neutralLayerCard,
        border: "none",
        display: "grid",
        padding: "8px 0",
        margin: "0 14px",
        width: "240px"
    },
    contextMenuItem: {
        background: neutralLayerCard,
        "box-sizing": "border-box",
        border: "none",
        display: "inline-block",
        padding: "6px 16px",
        width: "auto",
        "&:hover, &:focus": {
            background: neutralFillHover(neutralLayerCard),
            border: "none"
        },
        "&:active": {
            background: neutralFillActive(neutralLayerCard)
        }
    },
    contextMenuItem_contentRegion: {
        color: neutralForegroundRest(neutralLayerCard),
        "grid-column-start": "1"
    }
};